import { useDispatch, useSelector } from "react-redux";
import { CustomBox } from "../../commonElements";
import { BiMinus, BiPlus } from "react-icons/bi";
import { roomSelector, setRoomIsActive, triggerRoomApi, updateRoomCount } from "../rooms/roomSlice";
import { useEffect, useState } from "react";
import sweetAlert from "sweetalert";
import "../../components/sweetAlert/sweetAlert.css";

function GuestCount({ queryDetails, setQueryDetails, room, index, showRemoveButton }) {
  const isWebflowWebView = window.location.pathname.includes("webflowWebView");
  const dispatch = useDispatch();
  const { selectedRoom, roomCounts } = useSelector(roomSelector);
  
  // Get the current room data from Redux state
  const currentRoom = roomCounts[index] || room;
  
  // Destructure values based on the source
  let adults, children, pets;
  
  if (isWebflowWebView) {
    // For webflow, use queryDetails
    adults = queryDetails.adults;
    children = queryDetails.children;
    pets = queryDetails.pets;
  } else {
    // For normal flow, use room data from Redux
    adults = currentRoom?.adults || currentRoom?.adultsCount || 0;
    children = currentRoom?.children || currentRoom?.childrenCount || 0;
    pets = currentRoom?.pets || currentRoom?.petsCount || 0;
  }

  return (
    <CustomBox
      className="pl-[14px] pr-[14px] pt-[11px] pb-[11px] flex justify-between"
      sx={{ marginTop: "35px" }}
      index={index}
      showRemoveButton={showRemoveButton}
    >
      <Counter
        label={"ADULTS"}
        isRequired={true}
        keyName={"adults"}
        value={Number(adults)}
        queryDetails={queryDetails}
        setQueryDetails={setQueryDetails}
        minLimit={1}
        maxLimit={selectedRoom[0]?.maxAdultCount || 2}
        maxOccupancy={selectedRoom[0]?.maxOccupancy}
        sumOfAdultChild={Number(adults) + Number(children)}
        index={index}
        name={"adults"}
        isWebflowWebView={isWebflowWebView}
      />

      <Counter
        value={Number(children)}
        label={"CHILDREN"}
        keyName={"children"}
        queryDetails={queryDetails}
        setQueryDetails={setQueryDetails}
        minLimit={0}
        maxLimit={selectedRoom[0]?.maxChildCount || 2}
        maxOccupancy={selectedRoom[0]?.maxOccupancy}
        sumOfAdultChild={Number(adults) + Number(children)}
        index={index}
        name={"children"}
        isWebflowWebView={isWebflowWebView}
      />

      <Counter
        value={Number(pets)}
        label={"PETS"}
        keyName={"pets"}
        queryDetails={queryDetails}
        setQueryDetails={setQueryDetails}
        minLimit={0}
        maxLimit={selectedRoom[0]?.maxPetCount || 2}
        index={index}
        name={"pets"}
        isWebflowWebView={isWebflowWebView}
      />
    </CustomBox>
  );
}

function Counter({
  value,
  label,
  keyName,
  minLimit,
  maxLimit,
  queryDetails,
  maxOccupancy,
  setQueryDetails,
  sumOfAdultChild = false,
  index,
  name,
  isWebflowWebView,
}) {
  const dispatch = useDispatch();
  
  const handleDecrement = () => {
    if (value <= minLimit) return;
    
    const newValue = value - 1;
    
    if (isWebflowWebView) {
      // For webflow, update queryDetails directly
      queryDetails[keyName] = String(newValue);
      setQueryDetails({ ...queryDetails });
    } else {
      // For normal flow, update Redux state
      dispatch(setRoomIsActive(index));
      dispatch(updateRoomCount({ index, [name]: newValue }));
      // Trigger API call after a short delay to avoid multiple calls
      setTimeout(() => {
        dispatch(triggerRoomApi());
      }, 100);
    }
  };

  const handleIncrement = () => {
    if (value >= maxLimit || (sumOfAdultChild && sumOfAdultChild >= maxOccupancy)) {
      return;
    }
    
    const newValue = value + 1;
    
    if (isWebflowWebView) {
      // For webflow, update queryDetails directly
      queryDetails[keyName] = String(newValue);
      setQueryDetails({ ...queryDetails });
    } else {
      // For normal flow, update Redux state
      dispatch(setRoomIsActive(index));
      dispatch(updateRoomCount({ index, [name]: newValue }));
      // Trigger API call after a short delay to avoid multiple calls
      setTimeout(() => {
        dispatch(triggerRoomApi());
      }, 100);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <label className="text-[10px] uppercase">{label}</label>
      <div className="flex items-center flex-nowrap text-xl mt-3">
        <BiMinus
          onClick={handleDecrement}
          className={`text-xl mr-1 ${
            value == minLimit
              ? `text-gray-300 cursor-not-allowed`
              : `cursor-pointer`
          }`}
        />
        <span className="pt-[3px] leading-[17px] w-3">{value}</span>
        <BiPlus
          onClick={handleIncrement}
          className={`text-xl ml-1 ${
            value == maxLimit || sumOfAdultChild == maxOccupancy
              ? `text-gray-300 cursor-not-allowed`
              : `cursor-pointer`
          }`}
        />
      </div>
    </div>
  );
}

export default GuestCount;